import { AllowedEvent } from '@fhs-legacy/frontend/src/state/crm-events/types';

import { PaymentOrchestrator, PaymentPipelineError, PaymentPipelineResult } from '../../../payment';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';

// Error codes that indicate Google Pay or Apple Pay was cancelled or failed
// For these errors, we keep the payment method as-is and let the user choose to change it
const DIGITAL_WALLET_ERROR_CODES = new Set([
  // Google Pay errors
  'GOOGLE_PAY_CANCELLED',
  'GOOGLE_PAY_NO_DATA',
  'GOOGLE_PAY_REQUEST_FAILED',
  'GOOGLE_PAY_ERROR',
  'GOOGLE_PAY_API_NOT_LOADED',
  'GOOGLE_PAY_CONFIG_MISSING',
  // Apple Pay errors
  'APPLE_PAY_ERROR',
  'APPLE_PAY_NOT_AVAILABLE',
  'APPLE_PAY_NO_CARDS',
  'APPLE_PAY_AVAILABILITY_CHECK_FAILED',
  'APPLE_PAY_CONFIG_MISSING',
  'APPLE_PAY_API_NOT_LOADED',
  'APPLE_PAY_NOT_SUPPORTED',
]);

interface HandlePlaceOrderDependencies {
  lastResultRef: React.MutableRefObject<PaymentPipelineResult | null>;
  orchestratorRef: React.MutableRefObject<PaymentOrchestrator>;
  buildPipelineContext: () => any;
  setShouldSkipPriceOrder: (skip: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setCurrentError: (error: PaymentPipelineError | null) => void;
  logRBIEvent: (event: AllowedEvent) => void;
}

function isDigitalWalletError(errorCode: string | undefined): boolean {
  return !!errorCode && DIGITAL_WALLET_ERROR_CODES.has(errorCode);
}

export async function handlePlaceOrder({
  lastResultRef,
  orchestratorRef,
  setCurrentError,
  setIsProcessing,
  setShouldSkipPriceOrder,
  buildPipelineContext,
  logRBIEvent,
}: HandlePlaceOrderDependencies) {
  const { setRetryInfo, setCurrentState } = usePaymentFlowStore.getState();
  const lastResult = lastResultRef.current;
  const isRetry = !!lastResult?.error?.retryFromStep;

  // add logger before execution
  orchestratorRef.current.setCRMLogger(logRBIEvent);

  // Check context for fresh start
  if (!isRetry) {
    const pipelineContext = buildPipelineContext();
    if (!pipelineContext) {
      setCurrentError({
        code: 'CONTEXT_NOT_READY',
        message: 'Pipeline context is not ready',
        userMessage:
          'Unable to process payment. Please check your payment details and try again. Error Code: (Pipeline Context Readiness)',
      });
      return;
    }
  }

  // Reset state before execution
  setCurrentError(null);
  setIsProcessing(true);
  setRetryInfo(null);
  setCurrentState('idle');

  try {
    // Retry from specific step or start fresh
    // For certain errors, build fresh context (stale data may be the issue)
    const needsFreshContext =
      isRetry &&
      (lastResult.error?.code === 'MISSING_ENCRYPTION_RESULT' ||
        lastResult.error?.code === 'COMMIT_ORDER_FAILED');

    const result = isRetry && !needsFreshContext
      ? await orchestratorRef.current.execute(lastResult.context!, {
          skipToStep: lastResult.error!.retryFromStep,
        })
      : await orchestratorRef.current.execute(buildPipelineContext());

    lastResultRef.current = result;

    if (result.success) {
      handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      // TODO: do we need warnings for gift cards
      // handle warnings
      // if (result.error) {
      //   handleWarning(result, setCurrentError);
      // } else {
      //   handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      // }
    } else {
      handleError(result, setCurrentError, setRetryInfo);

      // For digital wallet errors (Google Pay/Apple Pay), clear the cached result so the next attempt builds a fresh context
      if (isDigitalWalletError(result.error?.code)) {
        lastResultRef.current = null;
      }
    }
  } catch (error) {
    handleUnexpectedError(error, setCurrentError, setRetryInfo);
  } finally {
    setIsProcessing(false);
  }
}

function handleSuccess(
  setShouldSkipPriceOrder: (skip: boolean) => void,
  setRetryInfo: (info: any) => void,
  setCurrentError: (error: PaymentPipelineError | null) => void
) {
  // Note: resetPaymentMethodStore is now called in the INSERT_SUCCESSFUL effect
  // to prevent the payment method from changing before navigation
  setShouldSkipPriceOrder(true);
  setRetryInfo(null);
  setCurrentError(null);
}

// TODO: do we need warnings for gift cards
// function handleWarning(
//   result: PaymentPipelineResult,
//   setCurrentError: (error: PaymentPipelineError | null) => void
// ) {
//   // Warnings are shown to the user but don't stop the flow
//   setCurrentError(result.error ?? null);
// }

function handleError(
  result: PaymentPipelineResult,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError(result.error ?? null);

  const errorCode = result.error?.code;

  // For digital wallet errors (Google Pay/Apple Pay cancelled or failed):
  // - Keep the current payment method (don't change it)
  // - Don't set retry info (user should manually select a different payment method)
  // - The error will clear when the user changes payment method (handled in PlaceOrderButton)
  if (isDigitalWalletError(errorCode)) {
    return;
  }

  if (result.error?.retryFromStep) {
    setRetryInfo({
      canRetry: true,
      retryFromStep: result.error.retryFromStep,
      errorCode: result.error.code,
      errorMessage: result.error.message,
      failedStep: result.failedStep,
    });
  }
}

function handleUnexpectedError(
  error: unknown,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError({
    code: 'UNEXPECTED_ERROR',
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage:
      'An unexpected error occurred. Please try again. Error Code: (Unexpected Orchestrator Error)',
  });
  setRetryInfo(null);
}
